import classNames from "classnames";
import groupBy from "lodash/groupBy";
import { ElementPill } from "PFApp/components/changelog/components/element_pill";
import { useExperienceConfig } from "PFApp/hooks/use_experience_config";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { useImportanceLabelMap } from "PFApp/hooks/use_importance_label_map";
import { Inline, Stack } from "PFComponents/containers";
import { Pill } from "PFComponents/pill";
import { Typography } from "PFComponents/typography";
import { CustomValuePill } from "PFCore/components/custom_value_pill/custom_value_pill";
import { CustomFieldValue } from "PFCore/types/changelog";
import { Id, Importance } from "PFTypes";
import { useTranslation } from "react-i18next";

import css from "./custom_field_change_group.module.scss";

type CustomFieldChangeGroupProps = {
  values: CustomFieldValue[];
  removedIds: Set<Id>;
  addedIds: Set<Id>;
  matchable: boolean;
  rankable: boolean;
  changedExperienceIds?: Set<Id>;
  isImportanceHighlighted?: (importance: Importance) => boolean;
  isAfterBlock: boolean;
};

export const CustomFieldChangeGroup = ({
  values,
  removedIds,
  addedIds,
  matchable,
  rankable,
  changedExperienceIds = new Set(),
  isImportanceHighlighted = () => false,
  isAfterBlock
}: CustomFieldChangeGroupProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "notificationChangelog" });
  const { experienceLevels } = useExperienceConfig();
  const experienceLabelMap = useExperienceLabelMap();
  const importanceLabelMap = useImportanceLabelMap();

  const showExperience = rankable && matchable;

  const renderItem = (item: CustomFieldValue) => (
    <Inline key={item.customValueId} gap="spacingXs">
      <ElementPill
        highlighted={showExperience && isAfterBlock && changedExperienceIds.has(item.customValueId)}
      >
        <CustomValuePill
          name={item.customValue}
          experienceLevels={experienceLevels}
          experienceLabelMap={experienceLabelMap}
          isRequirement
          {...(showExperience && {
            requiredExperience: item.requiredExperience
          })}
        />
      </ElementPill>
      {addedIds.has(item.customValueId) && <Pill text={t("added")} kind="success" small />}
      {removedIds.has(item.customValueId) && <Pill text={t("removed")} kind="danger" small />}
    </Inline>
  );

  if (!matchable) {
    return (
      <Inline wrap="wrap" gap="spacingXs">
        {values.map(renderItem)}
      </Inline>
    );
  }

  const groupedValues = groupBy(values, "importance");

  return (
    <Stack>
      {Object.entries(groupedValues).map(([importance, groupValues]) => {
        const importanceHighlighted = isAfterBlock && isImportanceHighlighted(Number(importance));
        return (
          <Stack key={importance}>
            <ElementPill highlighted={importanceHighlighted}>
              <Typography
                variant="labelRegular"
                className={classNames(css.importanceLabel, { [css.highlighted]: importanceHighlighted })}
              >
                {importanceLabelMap[importance]}
              </Typography>
            </ElementPill>
            <Inline wrap="wrap" gap="spacingXs">
              {groupValues.map(renderItem)}
            </Inline>
          </Stack>
        );
      })}
    </Stack>
  );
};
